import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { canBeSeenByNurse, canBeSeenByPhysician, ConsiderationGroupEnum } from '@app/advisor-shared';
import { _className, ComponentSync, LL_H_C, LL_V_L, TS_Input } from '@nu-art/thunderstorm-frontend/index';
import { FadeScrollWrapper, ICONS } from '@app/styles';
import { ModuleFE_Advisor_DiseaseProfile, ModuleFE_Advisor_Pathway, ModuleFE_Advisor_Variable, ModuleFE_AdvisorSession, ModuleFE_EMInterface, ModuleFE_RuntimeProtocol, StorageKey_AvailableProtocols, } from '@app/advisor-frontend';
import { arrayIncludesAny, BadImplementationException, Filter, filterInstances, sortArray } from '@nu-art/ts-common';
import './Renderer_AddProtocol.scss';
import { ModuleFE_ScrollBar } from '@app/styles/frontend/modules/ModuleFE_ScrollBar/ModuleFE_ScrollBar';
import { dispatch_OnAddProtocol } from './dispatchers.js';
import { PopUp_Confirmation } from '../../../../pop-ups/PopUp_Confirmation/PopUp_Confirmation.js';
import { ModuleFE_AdvisorAnalytics } from '../../../../../_analytics/consts.js';
import { ModuleFE_AdvisorMini } from '../../../../../modules/ModuleFE_AdvisorMini.js';
import { PanelKey_Home } from '../../../../../_shared.js';
var PathwaySortScore;
(function (PathwaySortScore) {
    PathwaySortScore[PathwaySortScore["ExactMatch"] = 0] = "ExactMatch";
    PathwaySortScore[PathwaySortScore["InitialExactMatch"] = 1] = "InitialExactMatch";
    PathwaySortScore[PathwaySortScore["StartsWith"] = 2] = "StartsWith";
    PathwaySortScore[PathwaySortScore["InitialStartsWith"] = 3] = "InitialStartsWith";
    PathwaySortScore[PathwaySortScore["Includes"] = 4] = "Includes";
    PathwaySortScore[PathwaySortScore["InitialIncludes"] = 5] = "InitialIncludes";
    PathwaySortScore[PathwaySortScore["Regex"] = 6] = "Regex";
})(PathwaySortScore || (PathwaySortScore = {}));
export class Renderer_AddProtocol extends ComponentSync {
    //######################### Life Cycle #########################
    deriveStateFromProps(nextProps, state) {
        state.consideration = nextProps.consideration;
        state.pathways = this.getPathways();
        return state;
    }
    componentDidMount() {
        ModuleFE_ScrollBar.updateScrollBar(this.props.scrollRef);
    }
    //######################### Logic #########################
    getPathways() {
        const existingProtocolIds = ModuleFE_RuntimeProtocol.cache.all().map(item => item.variableIds[0]);
        const providerType = ModuleFE_AdvisorSession.getUserType();
        const protocols = StorageKey_AvailableProtocols.get({
            succeeded: [],
            failed: [],
        });
        const filterProtocols = (id, available) => {
            const pathway = ModuleFE_Advisor_Pathway.cache.unique(id);
            if (!pathway) {
                this.logError(`Could not find pathway for id ${id}`);
                return;
            }
            if (arrayIncludesAny(existingProtocolIds, pathway.variableIds))
                return;
            switch (providerType) {
                case 'physician':
                    if (canBeSeenByPhysician(pathway.type))
                        break; // all good
                    return; // filter out what a physician may see
                case 'nurse':
                    if (canBeSeenByNurse(pathway.type))
                        break; // all good
                    return; // filter out what a nurse may see
                default:
                    throw new BadImplementationException(`Receive an unimplemented providerType, instead of "physician"/"nurse".`);
            }
            return { item: pathway, available: available };
        };
        const available = filterInstances(protocols.succeeded.map(id => filterProtocols(id, true)));
        const unavailable = filterInstances(protocols.failed.map(id => filterProtocols(id, false)));
        return sortArray([...available, ...unavailable], item => item.item.label.toLowerCase());
    }
    onPathwayClick = (e, pathway) => {
        if (!pathway.available)
            return;
        //Open confirmation dialog
        PopUp_Confirmation.show(e, {
            message: _jsxs(_Fragment, { children: ['Please confirm to proceed adding protocol ', _jsx("b", { children: pathway.item.label }), ` to ${this.state.consideration === ConsiderationGroupEnum.LOW_CONSIDERATION ? 'low' : 'high'} consideration`] }),
            acceptButtonLabel: 'Confirm',
            onAccept: async () => {
                try {
                    const diagnosis = ModuleFE_Advisor_Variable.cache.unique(pathway.item.variableIds[0]);
                    await ModuleFE_EMInterface.vv1.protocols.launchProtocol({
                        variableId: pathway.item.variableIds[0],
                        confidenceLevel: this.state.consideration,
                    }).executeSync();
                    //Report analytics event
                    if (diagnosis)
                        ModuleFE_AdvisorAnalytics.trigger.addData.addData_ddx_submit({
                            considerationLevel: this.state.consideration,
                            diagnosisId: diagnosis._id,
                            diagnosisLabel: diagnosis.name,
                        });
                }
                catch (err) {
                    this.logError(`Could not launch pathway ${pathway.item._id}`);
                }
                finally {
                    dispatch_OnAddProtocol.dispatchUI();
                    if (ModuleFE_AdvisorMini.getIsMiniActive())
                        ModuleFE_AdvisorMini.openPanel(PanelKey_Home);
                }
            }
        });
    };
    getPathwaysToRender = () => {
        const searchTerm = this.state.searchTerm?.toLowerCase();
        if (!searchTerm?.length)
            return this.state.pathways;
        const filter = new Filter(pathway => [pathway.item.label]);
        const pathways = filter.filter(this.state.pathways, searchTerm);
        const scoreMap = {};
        pathways.forEach(pathway => {
            const dpInitials = ModuleFE_Advisor_DiseaseProfile.cache
                .filter(dp => pathway.item.variableIds.includes(dp.variableId))
                .map(dp => dp.initials.toLowerCase());
            //Check pathway label exact matches term
            if (pathway.item.label === searchTerm)
                return scoreMap[pathway.item._id] = PathwaySortScore.ExactMatch;
            //Check associated disease profile initials exact matches term
            if (dpInitials.some(initial => initial === searchTerm))
                return scoreMap[pathway.item._id] = PathwaySortScore.InitialExactMatch;
            //Check pathway label starts with term
            if (pathway.item.label.startsWith(searchTerm))
                return scoreMap[pathway.item._id] = PathwaySortScore.StartsWith;
            //Check associated disease profile initials starts with term
            if (dpInitials.some(initial => initial.startsWith(searchTerm)))
                return scoreMap[pathway.item._id] = PathwaySortScore.InitialStartsWith;
            //Check pathway label includes term
            if (pathway.item.label.includes(searchTerm))
                return scoreMap[pathway.item._id] = PathwaySortScore.Includes;
            //Check associated disease profile initials includes term
            if (dpInitials.some(initial => initial.includes(searchTerm)))
                return scoreMap[pathway.item._id] = PathwaySortScore.InitialIncludes;
            //pathway matches regex wise
            scoreMap[pathway.item._id] = PathwaySortScore.Regex;
        });
        return sortArray(pathways, p => scoreMap[p.item._id]);
    };
    //######################### Render #########################
    render() {
        return _jsxs(LL_V_L, { className: 'add-protocol', children: [this.renderSearch(), this.renderProtocols()] });
    }
    renderSearch = () => {
        return _jsxs(LL_H_C, { className: 'add-protocol__search', children: [_jsx(TS_Input, { type: 'text', value: this.state.searchTerm, onChange: searchTerm => this.setState({ searchTerm }) }), _jsx(ICONS.advisor_v5_search.component, {})] });
    };
    renderProtocols = () => {
        const pathways = this.getPathwaysToRender();
        return _jsx(FadeScrollWrapper, { className: 'add-protocol__protocols', scrollRef: this.props.scrollRef, children: pathways.map(pathway => {
                const className = _className('add-protocol__protocol', !pathway.available && 'disabled');
                return _jsx("div", { className: className, onClick: e => this.onPathwayClick(e, pathway), children: pathway.item.label }, pathway.item._id);
            }) });
    };
}
//# sourceMappingURL=Renderer_AddProtocol.js.map